import { useEffect, useMemo, useState } from "react";
import {
  type DocumentReference,
  type DocumentSnapshot,
  type Query,
  type QuerySnapshot,
  onSnapshot,
  type FirestoreError,
} from "firebase/firestore";

export type FirestoreResult<T> = {
  data: (T & { id?: string }) | Array<T & { id?: string }> | null;
  loading: boolean;
  error: string | null;
};

type FirestoreTarget<T> = Query<T> | DocumentReference<T> | undefined;

type Snapshot<T> = QuerySnapshot<T> | DocumentSnapshot<T>;

function isQuerySnapshot<T>(snapshot: Snapshot<T>): snapshot is QuerySnapshot<T> {
  return "docs" in snapshot;
}

export function useFirestoreQuery<T>(target: FirestoreTarget<T>): FirestoreResult<T> {
  const [data, setData] = useState<FirestoreResult<T>["data"]>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(target));
  const [error, setError] = useState<string | null>(null);

  const memoizedTarget = useMemo(() => target, [target]);

  useEffect(() => {
    if (!memoizedTarget) {
      setData(null);
      setLoading(false);
      setError(null);
      return undefined;
    }

    setLoading(true);
    setError(null);

    let isActive = true;

    const unsubscribe = onSnapshot(
      memoizedTarget as any,
      { includeMetadataChanges: true },
      (snapshot: Snapshot<T>) => {
        if (!isActive) return; // Don't update state if component unmounted

        // Don't update routing-critical state until server confirms data
        // (first snapshot may come from local cache and may be stale)
        const fromCache = snapshot.metadata?.fromCache ?? false;
        
        if (isQuerySnapshot(snapshot)) {
          const documents = snapshot.docs.map((docSnapshot) => ({
            id: docSnapshot.id,
            ...(docSnapshot.data() as T),
          }));
          setData(documents);
          if (!fromCache) setLoading(false);
        } else {
          const exists = snapshot.exists();
          setData(
            exists
              ? ({
                  id: snapshot.id,
                  ...(snapshot.data() as T),
                } as T & { id?: string })
              : null
          );
          if (!fromCache) setLoading(false);
        }
      },
      (snapshotError: FirestoreError) => {
        if (!isActive) return; // Don't update state if component unmounted
        
        // Handle permission-denied errors gracefully (user logged out)
        if (snapshotError.code === 'permission-denied') {
          setData(null);
          setError(null); // Don't treat permission errors as errors when user is logged out
          setLoading(false);
        } else {
          setError(snapshotError.message);
          setLoading(false);
        }
      }
    );

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [memoizedTarget]);

  return { data, loading, error };
}
